/* SIZESOFTX
=========================================================================================
* Descripción: Contexto del módulo de compras
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 14/05/2026 |                                   | Creación
*      2       | 30/05/2026 | Lic. José Luis Aragón Cervantes    | Aplicación con inventario, etiquetas y crédito
*      3       | 30/05/2026 | Lic. José Luis Aragón Cervantes    | Validaciones previas al aplicar compra
*      4       | 30/05/2026 | Lic. José Luis Aragón Cervantes    | Persistencia cantidad solicitada
*      5       | 30/05/2026 | Lic. José Luis Aragón Cervantes    | Trazas diagnóstico aplicar compra
*      6       | 30/05/2026 | Lic. José Luis Aragón Cervantes    | Normalización payload aplicar compra
*      7       | 30/05/2026 | Lic. José Luis Aragón Cervantes    | Impresión etiquetas compra por medidas guardadas
*      8       | 30/05/2026 | Lic. José Luis Aragón Cervantes    | Etiquetado manual para compras aplicadas
*      9       | 30/05/2026 | Lic. José Luis Aragón Cervantes    | Integración obligatoria a inventario en aplicar compra
*      10      | 30/05/2026 | Lic. José Luis Aragón Cervantes    | Validación respuesta al actualizar cantidades de partida
*      11      | 30/05/2026 | Lic. José Luis Aragón Cervantes    | Destino de inventario seleccionado en compras
*      12      | 30/05/2026 | Lic. José Luis Aragón Cervantes    | Persistencia almacén exhibidor en cabecero
*      13      | 31/05/2026 | Lic. José Luis Aragón Cervantes    | Actualización robusta de cantidades en partidas
* ---------------------------------------------------------------------------------------
*/

import { createContext, useContext, useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    apiFolioCompra,
    apiRegistrarCabeceroCompra,
    apiRegistrarDetalleCompra,
    apiObtenerCompra,
    apiActualizarDetalleCompra,
    apiActualizarCorridaDetalleCompra,
    apiObtenerDatosMedida,
    apiEliminarPartidaCompra,
    apiEliminarCompraCompleta,
    apiAplicarCompra,
    apiListaCorridas,
    apiArticulosSimple,
    apiReporteCompra,
    apiBuscarCompras,
    apiProveedoresList,
    apiMontoALetra,
    apiMedidasCorrida,
    apiMedidasGuardadas,
    apiGuardarMedidas,
} from "../../settings/ApiConfig";

import { useRequest } from "../System/RequestContext";
import { useSesion } from "../System/SesionContext";
import { useLayout } from "../System/LayoutContext";
import { getDatePost } from "../../settings/utils";

const getInitialDataPartida = () => ({
    id: 0,
    clavearticulo: "",
    descripcion: "",
    corrida: "",
    formula: "",
    preciosugerido: 0,
    pmvb: 0,
    costocompra: 0,
    cantidadsolicitada: 0,
    cantidadrecibida: 0,
    costounitario: 0,
    subtotal: 0,
    iva: 0,
    total: 0,
});

export const ComprasContext = createContext();
export const ComprasProvider = (props) => {
    const { GetRequest, GetFileRequest, PostRequest, PutRequest, DeleteRequest } = useRequest();
    const { parametrosCajaForm, usuario } = useSesion();
    const { handleOpenAlert, handleOpenLoader } = useLayout();
    const navigate = useNavigate();

    const getPcTrabajo = () => parametrosCajaForm.caja?.trim().toUpperCase() || "";
    const getClaveProveedorCompra = () => proveedorSeleccionado?.claveproveedor?.trim() || "";

    const handleClickRegresarMainDashboard = () => {
        navigate("/");
    };

    const [folioCompra, setFolioCompra] = useState(0);
    const [statusCompra, setStatusCompra] = useState("AC");
    const [titleActionSave, setTitleActionSave] = useState("NUEVA");
    const [listaPartidasCompra, setListaPartidasCompra] = useState([]);
    const [partidaSeleccionada, setPartidaSeleccionada] = useState(null);
    const [statusWaiting, setStatusWaiting] = useState(false);
    const [modoLectura, setModoLectura] = useState(false);

    const [proveedorSeleccionado, setProveedorSeleccionado] = useState(() => {
        try {
            const stored = localStorage.getItem("compra_proveedor");
            return stored ? JSON.parse(stored) : null;
        } catch { return null; }
    });
    const [facturaRemision, setFacturaRemision] = useState(() => {
        try {
            return localStorage.getItem("compra_facturaremision") || "";
        } catch { return ""; }
    });
    const [tipoPago, setTipoPago] = useState("CONTADO");
    const [diasCredito, setDiasCredito] = useState(0);
    const [observaciones, setObservaciones] = useState("");
    const [almacenExhibidorCompra, setAlmacenExhibidorCompra] = useState(() => parametrosCajaForm.dominio?.trim().toUpperCase() || "");

    const [listaProveedores, setListaProveedores] = useState([]);
    const [listaCorridas, setListaCorridas] = useState([]);

    const [openModalBuscarArticulosSimple, setOpenModalBuscarArticulosSimple] = useState(false);
    const [openModalBuscarCompra, setOpenModalBuscarCompra] = useState(false);
    const [openModalReporte, setOpenModalReporte] = useState(false);
    const [pdfUrl, setPdfUrl] = useState("");
    const [medidasPartida, setMedidasPartida] = useState({});
    const [partidasLoadVersion, setPartidasLoadVersion] = useState(0);

    const [statusOpenDialog, setStatusOpenDialog] = useState(false);
    const [titleOpenDialog, setTitleOpenDialog] = useState("");
    const [messageOpenDialog, setMessageOpenDialog] = useState("");

    const [totalConLetra, setTotalConLetra] = useState("");

    const handleObtenerMontoLetra = async (monto) => {
        try {
            const url = apiMontoALetra.replace("{monto}", monto.toString());
            const result = await GetRequest({ url, loader: "" });
            if (!result.hasError && result.result) {
                setTotalConLetra(result.result);
            }
        } catch (error) {
            console.error("Error obteniendo monto en letra:", error);
        }
    };

    useEffect(() => {
        if (proveedorSeleccionado) {
            localStorage.setItem("compra_proveedor", JSON.stringify(proveedorSeleccionado));
        } else {
            localStorage.removeItem("compra_proveedor");
        }
    }, [proveedorSeleccionado]);

    useEffect(() => {
        localStorage.setItem("compra_facturaremision", facturaRemision || "");
    }, [facturaRemision]);

    const [compraInicializada, setCompraInicializada] = useState(false);

    const handleInicializarCompra = async () => {
        if (compraInicializada) return;
        const pc = getPcTrabajo();
        if (pc && pc.length > 0) {
            const nuevoFolio = await handleObtenerFolioDisponible(pc);
            setFolioCompra(nuevoFolio);
            if (nuevoFolio > 0) {
                const existe = await handleCargarCompra(nuevoFolio, pc);
                if (!existe) {
                    await handleGuardarCabecero(nuevoFolio, pc);
                }
            }
            setCompraInicializada(true);
        }
    };

    useEffect(() => {
        if (!usuario) {
            setCompraInicializada(false);
        }
    }, [usuario]);

    const totalesCompra = useMemo(() => {
        if (!listaPartidasCompra || listaPartidasCompra.length === 0) {
            return { totalRegistros: 0, cantidadArticulos: 0, subTotal: 0, totalIVA: 0, total: 0 };
        }
        const totalRegistros = listaPartidasCompra.length;
        const cantidadArticulos = listaPartidasCompra.reduce((sum, p) => sum + (parseFloat(p.cantidadrecibida) || 0), 0);
        const subTotal = listaPartidasCompra.reduce((sum, p) => sum + (parseFloat(p.subtotal) || 0), 0);
        const totalIVA = listaPartidasCompra.reduce((sum, p) => sum + (parseFloat(p.iva) || 0), 0);
        const total = listaPartidasCompra.reduce((sum, p) => sum + (parseFloat(p.total) || 0), 0);
        return { totalRegistros, cantidadArticulos, subTotal, totalIVA, total };
    }, [listaPartidasCompra]);

    useEffect(() => {
        if (totalesCompra.total > 0) {
            handleObtenerMontoLetra(totalesCompra.total);
        } else {
            setTotalConLetra("");
        }
    }, [totalesCompra.total]);

    const handleObtenerFolioDisponible = async (pc) => {
        try {
            const pcActual = pc || getPcTrabajo();
            
            if (!pcActual || pcActual.trim() === "") {
                handleOpenAlert("Error: No hay PC de trabajo configurada. Verifique los parámetros de caja.", "error");
                return 1;
            }
            
            const url = apiFolioCompra.replace("{pc}", pcActual);
            const result = await GetRequest({ url, loader: "Obteniendo folio..." });

            if (result.hasError) {
                handleOpenAlert("Error folio: " + result.message, "error");
                return 1;
            }
            return parseInt(result.result, 10) || 1;
        } catch (error) {
            handleOpenAlert("Error obteniendo folio: " + error, "error");
            return 0;
        }
    };

    const cabeceroTimeoutRef = useRef(null);

    const handleGuardarCabecero = useCallback(async (folio, pc) => {
        if (!folio || folio === 0) return;
        const pcActual = pc || getPcTrabajo();
        if (!pcActual) return;
        const payload = {
            foliocompra: folio,
            pc: pcActual,
            facturaremision: (facturaRemision || "").trim(),
            claveproveedor: (proveedorSeleccionado?.claveproveedor || "").trim(),
            razonsocial: (proveedorSeleccionado?.razonsocial || "").trim(),
            tipopago: tipoPago,
            observaciones: observaciones || "",
            diascredito: diasCredito || 0,
            almacenexhibidor: (almacenExhibidorCompra || "").trim().toUpperCase(),
            totalconletra: totalConLetra || "",
            subtotalcompra: totalesCompra.subTotal || 0,
            totalivacompra: totalesCompra.totalIVA || 0,
            totalcompra: totalesCompra.total || 0,
            statuscompra: "AC",
            tiporegistro: "F",
        };
        try {
            const result = await PostRequest({
                url: apiRegistrarCabeceroCompra,
                body: payload,
                loader: "",
            });
            if (result.hasError) {
                console.error("Error guardando cabecero:", result.message);
            }
        } catch (error) {
            console.error("Error guardando cabecero:", error);
        }
    }, [facturaRemision, proveedorSeleccionado, tipoPago, observaciones, diasCredito, almacenExhibidorCompra, totalesCompra, totalConLetra]);

    useEffect(() => {
        if (!folioCompra || folioCompra === 0) return;
        if (!compraInicializada) return;

        if (cabeceroTimeoutRef.current) {
            clearTimeout(cabeceroTimeoutRef.current);
        }
        cabeceroTimeoutRef.current = setTimeout(() => {
            handleGuardarCabecero(folioCompra, getPcTrabajo());
        }, 2000);

        return () => {
            if (cabeceroTimeoutRef.current) {
                clearTimeout(cabeceroTimeoutRef.current);
            }
        };
    }, [proveedorSeleccionado, facturaRemision, tipoPago, observaciones, diasCredito, folioCompra, compraInicializada]);

    const handleObtenerDatosCompra = async (folio) => {
        setStatusWaiting(true);
        try {
            if (!folio || folio === 0) {
                const nuevoFolio = await handleObtenerFolioDisponible(getPcTrabajo());
                setFolioCompra(nuevoFolio);
                setStatusCompra("AC");
                setTitleActionSave("NUEVA");
                setListaPartidasCompra([]);
                setPartidaSeleccionada(null);
                setProveedorSeleccionado(null);
                setFacturaRemision("");
                setTipoPago("CONTADO");
                setDiasCredito(0);
                setObservaciones("");
                setAlmacenExhibidorCompra(parametrosCajaForm.dominio?.trim().toUpperCase() || "");
                setModoLectura(false);
            } else {
                const url = apiObtenerCompra.replace("{folio}", folio).replace("{pc}", getPcTrabajo());
                const result = await GetRequest({ url, loader: "Cargando compra..." });
                if (result.hasError) {
                    handleOpenAlert(result.message || "Error al obtener la compra");
                    setStatusWaiting(false);
                    return;
                }
                const compra = result.result;
                setFolioCompra(compra.foliocompra || folio);
                setStatusCompra(compra.statuscompra || "AC");
                setTitleActionSave(compra.statuscompra === "TR" ? "TERMINADA" : "NUEVA");
                setListaPartidasCompra(compra.partidas || []);
                setProveedorSeleccionado(compra.claveproveedor ? { claveproveedor: compra.claveproveedor, razonsocial: compra.razonsocial || "" } : null);
                setFacturaRemision(compra.facturaremision || "");
                setTipoPago(compra.tipopago || "CONTADO");
                setDiasCredito(compra.diascredito || 0);
                setObservaciones(compra.observaciones || "");
                setAlmacenExhibidorCompra((compra.almacenexhibidor || parametrosCajaForm.dominio || "").trim().toUpperCase());
                setModoLectura(true);
                setPartidasLoadVersion(v => v + 1);
            }
        } catch (error) {
            console.error("Error obteniendo compra:", error);
            handleOpenAlert("Error al obtener la compra");
        }
        setStatusWaiting(false);
    };

    const handleCargarCompra = async (folio, pc) => {
        setStatusWaiting(true);
        try {
            const url = apiObtenerCompra.replace("{folio}", folio).replace("{pc}", pc || getPcTrabajo());
            const result = await GetRequest({ url, loader: "Cargando compra..." });
            if (result.hasError) {
                setStatusWaiting(false);
                return false;
            }
            const compra = result.result;
            setFolioCompra(compra.foliocompra || folio);
            setStatusCompra(compra.statuscompra || "AC");
            setTitleActionSave(compra.statuscompra === "TR" ? "TERMINADA" : "NUEVA");
            setListaPartidasCompra(compra.partidas || []);
            setProveedorSeleccionado(compra.claveproveedor ? { claveproveedor: compra.claveproveedor, razonsocial: compra.razonsocial || "" } : null);
            setFacturaRemision(compra.facturaremision || "");
            setTipoPago(compra.tipopago || "CONTADO");
            setDiasCredito(compra.diascredito || 0);
            setObservaciones(compra.observaciones || "");
            setAlmacenExhibidorCompra((compra.almacenexhibidor || parametrosCajaForm.dominio || "").trim().toUpperCase());
            setModoLectura(true);
            setPartidasLoadVersion(v => v + 1);
            setStatusWaiting(false);
            return true;
        } catch (error) {
            console.error("Error cargando compra:", error);
            setStatusWaiting(false);
            return false;
        }
    };

    const handleClickNuevaCompra = async () => {
        if (getPcTrabajo() === "CAJA-DEFAULT") return;
        setStatusCompra("AC");
        const nuevoFolio = await handleObtenerFolioDisponible(getPcTrabajo());
        setFolioCompra(nuevoFolio);
        setListaPartidasCompra([]);
        setPartidaSeleccionada(null);
        setProveedorSeleccionado(null);
        setFacturaRemision("");
        setTipoPago("CONTADO");
        setDiasCredito(0);
        setObservaciones("");
        setAlmacenExhibidorCompra(parametrosCajaForm.dominio?.trim().toUpperCase() || "");
        setModoLectura(false);
    };

    const handleClickGuardarCompra = async () => {
        if (!folioCompra || folioCompra === 0) return;
        setStatusWaiting(true);
        await handleGuardarCabecero(folioCompra, getPcTrabajo());
        setStatusWaiting(false);
        handleOpenAlert("Compra guardada correctamente", "ok");
    };

    const handleClickNuevaGuardarCompra = async (e) => {
        let title = e?.target?.innerText || titleActionSave;
        switch (title) {
            case "NUEVA":
                await handleClickNuevaCompra();
                setTitleActionSave("NUEVA");
                break;
        }
    };

    const handleGuardarCompra = async () => {
        if (!folioCompra || folioCompra === 0) return;
        setStatusWaiting(true);
        await handleGuardarCabecero(folioCompra, getPcTrabajo());
        setModoLectura(true);
        setTitleActionSave("NUEVA");
        setStatusWaiting(false);
        handleOpenAlert("Compra guardada correctamente", "ok");
    };

    const handleEditarCompra = () => {
        setModoLectura(false);
    };

    const handleObtenerProveedores = async () => {
        try {
            const result = await GetRequest({ url: apiProveedoresList, loader: "Cargando proveedores..." });
            if (result.hasError) {
                handleOpenAlert(result.message || "Error al obtener proveedores");
                return;
            }
            setListaProveedores(Array.isArray(result.result) ? result.result : []);
        } catch (error) {
            console.error("Error obteniendo proveedores:", error);
            handleOpenAlert("Error al obtener proveedores");
        }
    };

    const handleObtenerCorridas = async () => {
        try {
            const result = await GetRequest({ url: apiListaCorridas, loader: "Cargando corridas..." });
            if (result.hasError) {
                console.error("Error obteniendo corridas:", result.message);
                return;
            }
            setListaCorridas(Array.isArray(result.result) ? result.result : []);
        } catch (error) {
            console.error("Error obteniendo corridas:", error);
        }
    };

    const handleChangeProveedor = (event, newValue) => {
        setProveedorSeleccionado(newValue);
    };

    const handleChangeFacturaRemision = (value) => {
        setFacturaRemision(value);
    };

    const handleChangeTipoPago = (value) => {
        setTipoPago(value);
        if (value !== "CREDITO") {
            setDiasCredito(0);
        }
    };

    const handleChangeDiasCredito = (value) => {
        setDiasCredito(value);
    };

    const handleChangeObservaciones = (value) => {
        setObservaciones(value);
    };

    const handleChangeAlmacenExhibidorCompra = (value) => {
        setAlmacenExhibidorCompra((value || "").trim().toUpperCase());
    };

    const handleChangePartidaSeleccionada = (row) => {
        setPartidaSeleccionada(row);
    };

    const handleBuscarArticuloSimple = async () => {
        setOpenModalBuscarArticulosSimple(true);
    };

    const handleCloseBuscarArticuloSimple = () => {
        setOpenModalBuscarArticulosSimple(false);
    };

    const handleSelectArticuloSimple = async (articulo) => {
        await handleRegistrarArticuloCompra(articulo);
    };

    const calcularPrecioSugerido = (costounitario, formula, factor) => {
        if (!costounitario || !formula) return 0;
        try {
            const margen = parseFloat(formula) || 0;
            return costounitario * (1 + margen / 100);
        } catch {
            return 0;
        }
    };

    const recalcularFila = (partida) => {
        const costounitario = parseFloat(partida.costounitario) || 0;
        const cantidadrecibida = parseFloat(partida.cantidadrecibida) || 0;
        const iva = partida.statusivacompra === "SI" ? 0.16 : 0;
        
        const subtotal = costounitario * cantidadrecibida;
        const ivacalc = subtotal * iva;
        const total = subtotal + ivacalc;

        return {
            ...partida,
            subtotal: subtotal,
            iva: ivacalc,
            total: total,
        };
    };

    const normalizarClavePartida = (value) => (value || "").toString().trim().toUpperCase();

    const esMismaPartidaCompra = (partida, row) => {
        if (!partida || !row) return false;
        if (partida.id !== undefined && row.id !== undefined && partida.id === row.id) return true;

        return Number(partida.foliocompra || folioCompra) === Number(row.foliocompra || folioCompra)
            && normalizarClavePartida(partida.pc || getPcTrabajo()) === normalizarClavePartida(row.pc || getPcTrabajo())
            && normalizarClavePartida(partida.clavearticulo) === normalizarClavePartida(row.clavearticulo)
            && normalizarClavePartida(partida.corrida) === normalizarClavePartida(row.corrida);
    };

    const obtenerPartidaActual = (row) => (
        listaPartidasCompra.find((partida) => esMismaPartidaCompra(partida, row)) || row
    );

    const handleRegistrarArticuloCompra = async (articulo) => {
        if (statusCompra === "TR") {
            handleOpenAlert("warning", "La compra está terminada, no se pueden agregar artículos");
            return;
        }

        setStatusWaiting(true);
        try {
            const pc = getPcTrabajo();

            const payloadCabecera = {
                foliocompra: folioCompra,
                pc: pc,
                facturaremision: (facturaRemision || "").trim(),
                claveproveedor: (proveedorSeleccionado?.claveproveedor || "").trim(),
                razonsocial: (proveedorSeleccionado?.razonsocial || "").trim(),
                tipopago: tipoPago,
                observaciones: observaciones || "",
                diascredito: diasCredito || 0,
                almacenexhibidor: (almacenExhibidorCompra || "").trim().toUpperCase(),
                subtotalcompra: totalesCompra.subTotal || 0,
                totalivacompra: totalesCompra.totalIVA || 0,
                totalcompra: totalesCompra.total || 0,
                statuscompra: "AC",
                tiporegistro: "F",
            };

            const resultCabecera = await PostRequest({
                url: apiRegistrarCabeceroCompra,
                body: payloadCabecera,
                loader: "Registrando cabecera...",
            });

            if (resultCabecera.hasError) {
                handleOpenAlert(resultCabecera.message || "Error al registrar cabecera");
                setStatusWaiting(false);
                return;
            }

            const statusIvaCompra = (articulo.exentoiva?.toUpperCase() === "NO") ? "SI" : "NO";

            const payloadDetalle = {
                foliocompra: folioCompra,
                pc: pc,
                clavearticulo: articulo.clavearticulo || articulo.clave || "",
                descripcion: articulo.descripcion || "",
                cantidadsolicitada: 1,
                cantidadrecibida: 1,
                costounitario: articulo.costounitario || 0,
                formula: articulo.formula || articulo.corrida || "",
                corrida: articulo.corrida || "",
                pmvb: articulo.pmvb || 0,
                costocompra: articulo.costocompra || 0,
                preciosugerido: articulo.preciosugerido || 0,
                statusivacompra: statusIvaCompra,
                subtotal: 0,
                iva: 0,
                total: 0,
            };

            const result = await PostRequest({
                url: apiRegistrarDetalleCompra,
                body: payloadDetalle,
                loader: "Registrando artículo...",
            });
            if (result.hasError) {
                handleOpenAlert(result.message || "Error al registrar artículo");
                setStatusWaiting(false);
                return;
            }
            const nuevaPartida = recalcularFila({
                id: result.result?.id || Date.now(),
                ...payloadDetalle,
                ...result.result,
            });
            setListaPartidasCompra((prev) => [...prev, nuevaPartida]);
            setPartidasLoadVersion(v => v + 1);
            handleOpenAlert("Artículo registrado correctamente", "ok");
        } catch (error) {
            console.error("Error registrando artículo:", error);
            handleOpenAlert("Error al registrar artículo");
        }
        setStatusWaiting(false);
    };

    const handleActualizarPartidaCompra = async (row, updates) => {
        if (statusCompra === "TR") return;

        if (updates.corrida !== undefined) {
            const nuevaCorrida = (updates.corrida || "").trim().toUpperCase();
            const corridaOriginal = (row.corrida || "").trim().toUpperCase();

            // Si es idéntica, ignorar para evitar duplicar llamadas asíncronas
            if (nuevaCorrida === corridaOriginal) return;

            // Validación local: evitar registrar doblemente el mismo artículo con la misma corrida
            const esDuplicado = listaPartidasCompra.some(
                (p) => p.id !== row.id 
                    && (p.clavearticulo || "").trim().toUpperCase() === (row.clavearticulo || "").trim().toUpperCase()
                    && (p.corrida || "").trim().toUpperCase() === nuevaCorrida
            );

            if (esDuplicado) {
                handleOpenAlert("Ya existe un registro con esta corrida para este artículo en la compra actual", "warning");
                // Revertir el estado visual en la tabla de inmediato
                setListaPartidasCompra((prev) =>
                    prev.map((p) => (p.id === row.id ? { ...p, corrida: row.corrida } : p))
                );
                return;
            }
        }

        const partidaBase = obtenerPartidaActual(row);
        const partidaActualizada = { ...partidaBase, ...updates };
        const partidaRecalculada = recalcularFila(partidaActualizada);

        setListaPartidasCompra((prev) =>
            prev.map((p) => (esMismaPartidaCompra(p, row) ? partidaRecalculada : p))
        );

        if (updates.corrida !== undefined) {
            try {
                const corridaActual = (updates.corrida || "").trim();
                let formulaVal = "";
                let pmvbVal = 0;
                let costounitarioVal = 0;
                let preciosugeridoVal = 0;

                if (corridaActual) {
                    const urlDatosMedida = apiObtenerDatosMedida
                        .replace("{clavearticulo}", encodeURIComponent((row.clavearticulo || "").trim()))
                        .replace("{corrida}", encodeURIComponent(corridaActual));

                    const resultDatosMedida = await GetRequest({ url: urlDatosMedida, loader: "" });

                    const datosMedida = !resultDatosMedida.hasError && resultDatosMedida.result
                        ? resultDatosMedida.result
                        : null;

                    const tieneDatos = datosMedida?.hasData === true;

                    formulaVal = tieneDatos ? (datosMedida?.formula ?? "") : "";
                    pmvbVal = tieneDatos ? (datosMedida?.pmvb ?? 0) : 0;
                    costounitarioVal = tieneDatos ? (datosMedida?.cuc ?? 0) : 0;
                    preciosugeridoVal = tieneDatos ? (datosMedida?.preciosugerido ?? 0) : 0;
                }

                // Recalcular el precio sugerido de forma dinámica basándonos en la fórmula
                if (formulaVal && costounitarioVal > 0) {
                    try {
                        let expr = formulaVal
                            .replace(/costounitario/gi, String(costounitarioVal || 0))
                            .replace(/cuc/gi, String(costounitarioVal || 0))
                            .replace(/pmvb/gi, String(pmvbVal || 0));

                        if (/^[+-]?\d+(\.\d+)?$/.test(expr.trim())) {
                            const margin = parseFloat(expr) || 0;
                            preciosugeridoVal = (costounitarioVal || 0) * (1 + margin / 100);
                        } else {
                            const resultado = parseFloat(eval(expr));
                            preciosugeridoVal = isNaN(resultado) ? 0 : resultado;
                        }
                    } catch (err) {
                        console.error("Error recalculando precio sugerido al cambiar corrida:", err);
                    }
                }

                const partidaConDatosMedida = {
                    ...partidaRecalculada,
                    corrida: updates.corrida,
                    formula: formulaVal,
                    preciosugerido: preciosugeridoVal,
                    pmvb: pmvbVal,
                    costounitario: costounitarioVal,
                };

                const partidaActualizadaFinal = recalcularFila(partidaConDatosMedida);

                setListaPartidasCompra((prev) =>
                    prev.map((p) => (esMismaPartidaCompra(p, row) ? partidaActualizadaFinal : p))
                );

                const payload = {
                    foliocompra: folioCompra,
                    pc: getPcTrabajo(),
                    clavearticulo: row.clavearticulo,
                    corrida: updates.corrida,
                    corridaoriginal: row.corrida || "",
                    formula: formulaVal,
                    preciosugerido: preciosugeridoVal,
                    pmvb: pmvbVal,
                    costounitario: costounitarioVal,
                    id: row.id || 0,
                };
                const result = await PostRequest({
                    url: apiActualizarCorridaDetalleCompra,
                    body: payload,
                });
                if (result.hasError) {
                    handleOpenAlert(result.message || "Error al actualizar corrida", "warning");
                    setListaPartidasCompra((prev) =>
                        prev.map((p) => (esMismaPartidaCompra(p, row) ? { ...p, corrida: row.corrida } : p))
                    );
                    return;
                }

                // Cargar definiciones de medidas de la corrida + valores guardados
                try {
                    const nuevaCorrida = (updates.corrida || "").trim();
                    if (!nuevaCorrida) {
                        setMedidasPartida((prev) => ({ ...prev, [row.id]: [] }));
                    } else {
                        const urlMedidas = apiMedidasCorrida
                            .replace("{clavearticulo}", encodeURIComponent((row.clavearticulo || "").trim()))
                            .replace("{corrida}", encodeURIComponent(nuevaCorrida));
                        const resultMedidas = await GetRequest({ url: urlMedidas, loader: "" });
                        const definiciones = !resultMedidas.hasError && Array.isArray(resultMedidas.result)
                            ? resultMedidas.result
                            : [];

                        const urlGuardadas = apiMedidasGuardadas
                            .replace("{folio}", folioCompra)
                            .replace("{pc}", getPcTrabajo())
                            .replace("{clave}", encodeURIComponent((row.clavearticulo || "").trim()))
                            .replace("{corrida}", encodeURIComponent(nuevaCorrida));
                        const resultGuardadas = await GetRequest({ url: urlGuardadas, loader: "" });
                        const guardadas = !resultGuardadas.hasError && Array.isArray(resultGuardadas.result)
                            ? resultGuardadas.result
                            : [];

                        const medidas = definiciones.map((m) => {
                            const guardada = guardadas.find((g) => Math.abs(g.medida - m.medida) < 0.001);
                            return { medida: m.medida, cantidad: guardada ? guardada.cantidad : 0 };
                        });

                        setMedidasPartida((prev) => ({ ...prev, [row.id]: medidas }));
                    }
                } catch (err) {
                    console.error("Error cargando medidas:", err);
                }
            } catch (error) {
                console.error("Error actualizando corrida:", error);
            }
        }

        if (updates.costounitario !== undefined || updates.cantidadsolicitada !== undefined || updates.cantidadrecibida !== undefined) {
            try {
                const payload = {
                    foliocompra: folioCompra,
                    pc: getPcTrabajo(),
                    clavearticulo: partidaBase.clavearticulo,
                    cantidadsolicitada: partidaRecalculada.cantidadsolicitada,
                    cantidadrecibida: partidaRecalculada.cantidadrecibida,
                    costounitario: partidaRecalculada.costounitario,
                    subtotal: partidaRecalculada.subtotal,
                    iva: partidaRecalculada.iva,
                    total: partidaRecalculada.total,
                    corrida: partidaBase.corrida || "",
                    pmvb: partidaRecalculada.pmvb || 0,
                    formula: partidaRecalculada.formula || "",
                    preciosugerido: partidaRecalculada.preciosugerido || 0,
                    id: partidaBase.id || 0,
                };
                const result = await PostRequest({
                    url: apiActualizarDetalleCompra,
                    body: payload,
                });
                if (result.hasError) {
                    console.error("[FrmCompras][ActualizarPartida] Error API", { result, payload });
                    handleOpenAlert(result.message || "Error al actualizar cantidades de la partida", "warning");
                    setListaPartidasCompra((prev) =>
                        prev.map((p) => (esMismaPartidaCompra(p, row) ? partidaBase : p))
                    );
                    return;
                }
            } catch (error) {
                console.error("Error actualizando partida:", error);
                setListaPartidasCompra((prev) =>
                    prev.map((p) => (esMismaPartidaCompra(p, row) ? partidaBase : p))
                );
            }
        }

        if (updates.formula !== undefined) {
            try {
                const precioSugeridoCalculado = updates.preciosugerido !== undefined
                    ? updates.preciosugerido
                    : null;

                const partidaConDatosMedida = {
                    ...partidaRecalculada,
                    formula: updates.formula,
                    preciosugerido: precioSugeridoCalculado ?? partidaRecalculada.preciosugerido,
                };

                const partidaActualizadaFinal = recalcularFila(partidaConDatosMedida);

                setListaPartidasCompra((prev) =>
                    prev.map((p) => (p.id === row.id ? partidaActualizadaFinal : p))
                );

                const payload = {
                    foliocompra: folioCompra,
                    pc: getPcTrabajo(),
                    clavearticulo: row.clavearticulo,
                    corrida: row.corrida || "",
                    corridaoriginal: row.corrida || "",
                    formula: updates.formula,
                    preciosugerido: precioSugeridoCalculado ?? 0,
                    pmvb: row.pmvb || 0,
                    costounitario: row.costounitario || 0,
                };
                const result = await PostRequest({
                    url: apiActualizarCorridaDetalleCompra,
                    body: payload,
                });
                if (result.hasError) {
                    handleOpenAlert(result.message || "Error al actualizar fórmula", "warning");
                    setListaPartidasCompra((prev) =>
                        prev.map((p) => (p.id === row.id ? { ...p, formula: row.formula, preciosugerido: row.preciosugerido } : p))
                    );
                    return;
                }
            } catch (error) {
                console.error("Error actualizando fórmula:", error);
            }
        }

        if (updates.pmvb !== undefined) {
            try {
                const formula = row.formula || "";
                const nuevoPmvb = updates.pmvb;
                let nuevoPrecioSugerido = row.preciosugerido;

                if (formula) {
                    try {
                        let expr = formula
                            .replace(/costounitario/gi, String(row.costounitario || 0))
                            .replace(/cuc/gi, String(row.costounitario || 0))
                            .replace(/pmvb/gi, String(nuevoPmvb || 0));

                        if (/^[+-]?\d+(\.\d+)?$/.test(expr.trim())) {
                            const margin = parseFloat(expr) || 0;
                            nuevoPrecioSugerido = (row.costounitario || 0) * (1 + margin / 100);
                        } else {
                            const resultado = parseFloat(eval(expr));
                            nuevoPrecioSugerido = isNaN(resultado) ? 0 : resultado;
                        }
                    } catch (err) {
                        console.error("Error recalculando precio sugerido en PMVB:", err);
                    }
                }

                const partidaConDatosMedida = {
                    ...partidaRecalculada,
                    pmvb: nuevoPmvb,
                    preciosugerido: nuevoPrecioSugerido,
                };

                const partidaActualizadaFinal = recalcularFila(partidaConDatosMedida);

                setListaPartidasCompra((prev) =>
                    prev.map((p) => (p.id === row.id ? partidaActualizadaFinal : p))
                );

                const payload = {
                    foliocompra: folioCompra,
                    pc: getPcTrabajo(),
                    clavearticulo: row.clavearticulo,
                    corrida: row.corrida || "",
                    corridaoriginal: row.corrida || "",
                    formula: row.formula || "",
                    preciosugerido: nuevoPrecioSugerido,
                    pmvb: nuevoPmvb,
                    costounitario: row.costounitario || 0,
                };
                const result = await PostRequest({
                    url: apiActualizarCorridaDetalleCompra,
                    body: payload,
                });
                if (result.hasError) {
                    handleOpenAlert(result.message || "Error al actualizar PMVB", "warning");
                    setListaPartidasCompra((prev) =>
                        prev.map((p) => (p.id === row.id ? { ...p, pmvb: row.pmvb, preciosugerido: row.preciosugerido } : p))
                    );
                    return;
                }
            } catch (error) {
                console.error("Error actualizando PMVB:", error);
            }
        }
    };

    const handleEliminarPartidaCompra = async (partida) => {
        if (!partida) return;
        if (statusCompra === "TR") {
            handleOpenAlert("warning", "La compra está terminada, no se pueden eliminar partidas");
            return;
        }

        setStatusWaiting(true);
        try {
            const url = `${apiEliminarPartidaCompra
                .replace("{folio}", folioCompra)
                .replace("{pc}", getPcTrabajo())
                .replace("{clave}", partida.clavearticulo)
                .replace("{corrida}", partida.corrida || "")}`;
            const result = await DeleteRequest({ url, loader: "Eliminando partida..." });
            if (result.hasError) {
                handleOpenAlert(result.message || "Error al eliminar partida");
                setStatusWaiting(false);
                return;
            }
            setListaPartidasCompra((prev) => prev.filter((p) => p.id !== partida.id));
            handleOpenAlert("Partida eliminada correctamente", "ok");
        } catch (error) {
            console.error("Error eliminando partida:", error);
            handleOpenAlert("Error al eliminar partida");
        }
        setStatusWaiting(false);
    };

    const handleCargarMedidasPartida = useCallback(async (partida) => {
        if (!partida || !partida.corrida || !partida.clavearticulo) return;
        try {
            const urlMedidas = apiMedidasCorrida
                .replace("{clavearticulo}", encodeURIComponent((partida.clavearticulo || "").trim()))
                .replace("{corrida}", encodeURIComponent((partida.corrida || "").trim()));
            const resultMedidas = await GetRequest({ url: urlMedidas, loader: "" });
            if (resultMedidas.hasError || !Array.isArray(resultMedidas.result)) return;

            const urlGuardadas = apiMedidasGuardadas
                .replace("{folio}", folioCompra)
                .replace("{pc}", getPcTrabajo())
                .replace("{clave}", encodeURIComponent((partida.clavearticulo || "").trim()))
                .replace("{corrida}", encodeURIComponent((partida.corrida || "").trim()));
            const resultGuardadas = await GetRequest({ url: urlGuardadas, loader: "" });
            const guardadas = !resultGuardadas.hasError && Array.isArray(resultGuardadas.result)
                ? resultGuardadas.result
                : [];

            const medidas = resultMedidas.result.map((m) => {
                const guardada = guardadas.find((g) => Math.abs(g.medida - m.medida) < 0.001);
                return {
                    medida: m.medida,
                    cantidad: guardada ? guardada.cantidad : 0,
                    codigobarras: m.codigobarras || "",
                };
            });

            setMedidasPartida((prev) => ({ ...prev, [partida.id]: medidas }));
        } catch (err) {
            console.error("Error cargando medidas de partida:", err);
        }
    }, [folioCompra, GetRequest]);

    const handleActualizarCantidadMedida = async (partida, index, nuevaCantidad) => {
        const medidasActuales = medidasPartida[partida.id] || [];
        const medidasActualizadas = medidasActuales.map((m, i) => ({
            ...m,
            cantidad: i === index ? nuevaCantidad : m.cantidad,
        }));

        setMedidasPartida((prev) => ({ ...prev, [partida.id]: medidasActualizadas }));

        // Guardar las medidas actualizadas
        try {
            const url = apiGuardarMedidas
                .replace("{folio}", folioCompra)
                .replace("{pc}", getPcTrabajo())
                .replace("{clave}", encodeURIComponent((partida.clavearticulo || "").trim()))
                .replace("{corrida}", encodeURIComponent((partida.corrida || "").trim()));
            await PostRequest({ url, body: medidasActualizadas, loader: "" });
        } catch (err) {
            console.error("Error guardando medidas:", err);
        }

        // Sumar medidas y actualizar cantidad recibida
        const totalMedidas = medidasActualizadas.reduce((sum, m) => sum + (m.cantidad || 0), 0);
        const partidaActualizada = recalcularFila({ ...partida, cantidadrecibida: totalMedidas });

        setListaPartidasCompra((prev) =>
            prev.map((p) => (p.id === partida.id ? partidaActualizada : p))
        );

        try {
            const payload = {
                foliocompra: folioCompra,
                pc: getPcTrabajo(),
                clavearticulo: partida.clavearticulo,
                cantidadsolicitada: partida.cantidadsolicitada,
                cantidadrecibida: totalMedidas,
                costounitario: partida.costounitario,
                subtotal: partidaActualizada.subtotal,
                iva: partidaActualizada.iva,
                total: partidaActualizada.total,
                corrida: partida.corrida || "",
                pmvb: partida.pmvb || 0,
                formula: partida.formula || "",
                preciosugerido: partida.preciosugerido || 0,
            };
            await PostRequest({ url: apiActualizarDetalleCompra, body: payload, loader: "" });
        } catch (err) {
            console.error("Error actualizando cantidad recibida por medidas:", err);
        }
    };

    const handleEliminarCompraCompleta = async () => {
        if (statusCompra === "TR") {
            handleOpenAlert("warning", "La compra está terminada, no se puede eliminar");
            return;
        }

        setStatusWaiting(true);
        try {
            const url = `${apiEliminarCompraCompleta
                .replace("{folio}", folioCompra)
                .replace("{pc}", getPcTrabajo())}`;
            const result = await DeleteRequest({ url, loader: "Eliminando compra..." });
            if (result.hasError) {
                handleOpenAlert(result.message || "Error al eliminar compra");
                setStatusWaiting(false);
                return;
            }
            await handleObtenerDatosCompra(0);
            handleOpenAlert("Compra eliminada correctamente", "ok");
        } catch (error) {
            console.error("Error eliminando compra:", error);
            handleOpenAlert("Error al eliminar compra");
        }
        setStatusWaiting(false);
    };

    const obtenerMedidasEtiquetasCompra = async (partida) => {
        if (!partida || !partida.corrida || !partida.clavearticulo) return [];

        const medidasLocales = medidasPartida[partida.id] || [];
        if (medidasLocales.some((medida) => Number(medida.cantidad) > 0 && (medida.codigobarras || medida.codigoBarras))) {
            return medidasLocales;
        }

        const urlMedidas = apiMedidasCorrida
            .replace("{clavearticulo}", encodeURIComponent((partida.clavearticulo || "").trim()))
            .replace("{corrida}", encodeURIComponent((partida.corrida || "").trim()));
        const resultMedidas = await GetRequest({ url: urlMedidas, loader: "" });
        if (resultMedidas.hasError || !Array.isArray(resultMedidas.result)) return medidasLocales;

        const urlGuardadas = apiMedidasGuardadas
            .replace("{folio}", folioCompra)
            .replace("{pc}", getPcTrabajo())
            .replace("{clave}", encodeURIComponent((partida.clavearticulo || "").trim()))
            .replace("{corrida}", encodeURIComponent((partida.corrida || "").trim()));
        const resultGuardadas = await GetRequest({ url: urlGuardadas, loader: "" });
        const guardadas = !resultGuardadas.hasError && Array.isArray(resultGuardadas.result)
            ? resultGuardadas.result
            : [];

        const medidas = resultMedidas.result.map((m) => {
            const guardada = guardadas.find((g) => Math.abs(Number(g.medida) - Number(m.medida)) < 0.001);
            return {
                medida: m.medida,
                cantidad: guardada ? guardada.cantidad : 0,
                codigobarras: m.codigobarras || m.codigoBarras || "",
            };
        });

        setMedidasPartida((prev) => ({ ...prev, [partida.id]: medidas }));
        return medidas;
    };

    const buildLineasEtiquetasCompra = async () => {
        const lineas = [];

        for (const partida of (listaPartidasCompra || [])) {
            const medidas = await obtenerMedidasEtiquetasCompra(partida);

            if (medidas.length > 0) {
                medidas.forEach((medida) => {
                    const total = Number(medida.cantidad) || 0;
                    if (total <= 0) return;

                    lineas.push({
                        clavearticulo: partida.clavearticulo || "",
                        descripcionArticulo: partida.descripcion || "",
                        medida: medida.medida,
                        totalEtiquetas: total,
                        codigoBarras: medida.codigobarras || medida.codigoBarras || "",
                    });
                });
                continue;
            }

            const total = Number(partida.cantidadrecibida) || 0;
            if (total > 0) {
                lineas.push({
                    clavearticulo: partida.clavearticulo || "",
                    descripcionArticulo: partida.descripcion || "",
                    medida: 0,
                    totalEtiquetas: total,
                    codigoBarras: partida.codigobarras || "",
                });
            }
        }

        return lineas;
    };

    const handleImprimirEtiquetasCompra = async () => {
        try {
            const lineas = await buildLineasEtiquetasCompra();
            const lineasConCantidad = lineas.filter(linea => Number(linea.totalEtiquetas) > 0);

            if (lineasConCantidad.length === 0) {
                handleOpenAlert("No hay etiquetas por imprimir", "warning");
                return false;
            }

            const lineasSinCodigo = lineasConCantidad.filter(linea => !linea.codigoBarras);
            if (lineasSinCodigo.length > 0) {
                handleOpenAlert("Hay medidas sin código de barras. Revise el catálogo antes de imprimir etiquetas.", "warning");
                return false;
            }

            let printerName = parametrosCajaForm?.nombreimpresoraetiqueta?.trim() || "";
            if (!printerName) {
                printerName = localStorage.getItem("nombreimpresoraetiqueta") || "";
            }

            if (!printerName) {
                handleOpenAlert("No se encontró la impresora de etiquetas configurada", "warning");
                return false;
            }

            localStorage.setItem("nombreimpresoraetiqueta", printerName);

            for (const linea of lineasConCantidad) {
                const formData = new FormData();
                formData.append("total_etiquetas", Number(linea.totalEtiquetas) || 0);
                formData.append("printerName", printerName);
                formData.append("descripcion_articulo", linea.descripcionArticulo);
                formData.append("medida", linea.medida);
                formData.append("codigo_barras", linea.codigoBarras);

                const response = await fetch("http://localhost:4000/api/imprimiretiquetas", {
                    method: "POST",
                    body: formData,
                });

                if (!response.ok) {
                    const text = await response.text().catch(() => response.statusText);
                    handleOpenAlert(`Error en impresión de etiquetas: ${text}`, "error");
                    return false;
                }

                await new Promise(resolve => setTimeout(resolve, 1000));
            }

            handleOpenAlert("Impresión de etiquetas realizada", "ok");
            return true;
        } catch (error) {
            handleOpenAlert(`PrinterTools no disponible: ${error.message}`, "error");
            return false;
        }
    };

    const normalizarPartidaAplicarCompra = (partida) => ({
        id: partida.id || 0,
        foliocompra: partida.foliocompra || folioCompra,
        pc: (partida.pc || getPcTrabajo()).trim(),
        clavearticulo: (partida.clavearticulo || "").trim(),
        descripcion: partida.descripcion || "",
        corrida: (partida.corrida || "").trim(),
        corridaoriginal: (partida.corridaoriginal || partida.corrida || "").trim(),
        formula: partida.formula || "",
        preciosugerido: Number(partida.preciosugerido) || 0,
        pmvb: Number(partida.pmvb) || 0,
        costocompra: Number(partida.costocompra) || 0,
        cantidadsolicitada: Number(partida.cantidadsolicitada) || 0,
        fecharecepcion: partida.fecharecepcion || null,
        statusrecepcion: partida.statusrecepcion || "PENDIENTE",
        cantidadrecibida: Number(partida.cantidadrecibida) || 0,
        costounitario: Number(partida.costounitario) || 0,
        statusivacompra: partida.statusivacompra || "NO",
        subtotal: Number(partida.subtotal) || 0,
        iva: Number(partida.iva) || 0,
        total: Number(partida.total) || 0,
    });

    const handleAplicarCompra = async (almacenDestino = almacenExhibidorCompra) => {
        if (!listaPartidasCompra || listaPartidasCompra.length === 0) {
            handleOpenAlert("No hay partidas para aplicar", "warning");
            return;
        }
        if (statusCompra === "TR") {
            handleOpenAlert("La compra ya está aplicada", "warning");
            return;
        }
        const almacenInventario = (almacenDestino || "").trim().toUpperCase();
        if (!almacenInventario) {
            handleOpenAlert("Seleccione el almacén/exhibidor destino antes de aplicar la compra", "warning");
            return;
        }
        if ((tipoPago || "").trim().toUpperCase() === "CREDITO" && !getClaveProveedorCompra()) {
            handleOpenAlert("Seleccione un proveedor para registrar la cuenta por pagar", "warning");
            return;
        }

        setStatusWaiting(true);
        try {
            const folioAplicado = folioCompra;
            const pcAplicada = getPcTrabajo();
            const proveedorAplicar = {
                claveproveedor: (proveedorSeleccionado?.claveproveedor || "").trim(),
                razonsocial: (proveedorSeleccionado?.razonsocial || "").trim(),
            };
            const payload = {
                foliocompra: folioAplicado,
                pc: pcAplicada,
                proveedor: proveedorAplicar,
                facturaremision: (facturaRemision || "").trim(),
                tipopago: (tipoPago || "").trim(),
                diascredito: diasCredito,
                observaciones: observaciones || "",
                almacenexhibidor: almacenInventario,
                totalconletra: totalConLetra || "",
                imprimiretiquetas: false,
                integrarinventario: true,
                usuario: usuario || "",
                partidas: listaPartidasCompra.map(normalizarPartidaAplicarCompra),
            };

            const url = apiAplicarCompra
                .replace("{folio}", folioAplicado)
                .replace("{pc}", pcAplicada);

            const result = await PostRequest({
                url,
                body: payload,
                loader: "Aplicando compra...",
            });


            if (result.hasError) {
                console.error("[FrmCompras][AplicarCompra] Error devuelto por API", {
                    message: result.message,
                    result,
                    payload,
                });
                handleOpenAlert(result.message || "Error al aplicar compra");
                setStatusWaiting(false);
                return;
            }
            setStatusCompra("TR");
            setTitleActionSave("TERMINADA");
            handleOpenAlert("Compra aplicada correctamente", "ok");

            await handleImprimirCompra(folioAplicado, pcAplicada);
             
            const nuevoFolio = await handleObtenerFolioDisponible(pcAplicada);
            setFolioCompra(nuevoFolio);
            setStatusCompra("AC");
            setTitleActionSave("NUEVA");
            setListaPartidasCompra([]);
            setPartidaSeleccionada(null);
            setProveedorSeleccionado(null);
            setFacturaRemision("");
            setTipoPago("CONTADO");
            setDiasCredito(0);
            setObservaciones("");
            setAlmacenExhibidorCompra(parametrosCajaForm.dominio?.trim().toUpperCase() || "");
        } catch (error) {
            console.error("[FrmCompras][AplicarCompra] Excepción aplicando compra", {
                error,
                folioCompra,
                pc: getPcTrabajo(),
                proveedorSeleccionado,
                tipoPago,
                listaPartidasCompra,
            });
            handleOpenAlert("Error al aplicar la compra");
        }
        setStatusWaiting(false);
    };

    const handleImprimirCompra = async (folioOverride, pcOverride) => {
        const folioReporte = folioOverride || folioCompra;
        const pcReporte = pcOverride || getPcTrabajo();

        if (!folioReporte || folioReporte === 0) {
            handleOpenAlert("warning", "No hay compra para imprimir");
            return;
        }

        try {
            const url = apiReporteCompra
                .replace("{folio}", folioReporte)
                .replace("{pc}", pcReporte);

            await GetFileRequest({
                url: url,
                filename: `ReporteCompra-${folioReporte}.pdf`,
                openInNewTab: true,
                loader: "Cargando reporte...",
            });
        } catch (error) {
            handleOpenAlert("Error al imprimir la compra", "error");
        }
        setStatusWaiting(false);
    };

    const handleRefreshDatos = async () => {
        await handleObtenerDatosCompra(folioCompra);
    };

    const handleOpenModalBuscarCompra = () => {
        setOpenModalBuscarCompra(true);
    };

    const handleCloseModalBuscarCompra = () => {
        setOpenModalBuscarCompra(false);
    };

    const handleConfirmDialog = (title) => {
        setTitleOpenDialog(title);
        if (title === "ELIMINAR PARTIDA") {
            setMessageOpenDialog("¿Está seguro de eliminar la partida seleccionada?");
        } else if (title === "ELIMINAR COMPRA") {
            setMessageOpenDialog("¿Está seguro de eliminar la compra completa?");
        }
        setStatusOpenDialog(true);
    };

    const value = {
        folioCompra,
        handleObtenerDatosCompra,
        handleCargarCompra,
        handleInicializarCompra,
        handleClickNuevaGuardarCompra,
        handleClickNuevaCompra,
        handleClickGuardarCompra,
        handleGuardarCompra,
        handleEditarCompra,
        modoLectura,
        handleClickRegresarMainDashboard,
        handleObtenerProveedores,
        listaProveedores,
        handleObtenerCorridas,
        listaCorridas,
        handleRegistrarArticuloCompra,
        handleActualizarPartidaCompra,
        handleEliminarPartidaCompra,
        handleEliminarCompraCompleta,
        handleAplicarCompra,
        handleImprimirEtiquetasCompra,
        handleImprimirCompra,
        handleRefreshDatos,
        handleOpenModalBuscarCompra,
        handleCloseModalBuscarCompra,
        openModalBuscarCompra,
        handleBuscarArticuloSimple,
        handleCloseBuscarArticuloSimple,
        openModalBuscarArticulosSimple,
        setOpenModalBuscarArticulosSimple,
        handleSelectArticuloSimple,
        partidaSeleccionada,
        handleChangePartidaSeleccionada,
        statusOpenDialog,
        setStatusOpenDialog,
        titleOpenDialog,
        setTitleOpenDialog,
        messageOpenDialog,
        setMessageOpenDialog,
        handleConfirmDialog,
        statusWaiting,
        listaPartidasCompra,
        totalesCompra,
        statusCompra,
        titleActionSave,
        proveedorSeleccionado,
        handleChangeProveedor,
        facturaRemision,
        handleChangeFacturaRemision,
        tipoPago,
        handleChangeTipoPago,
        diasCredito,
        handleChangeDiasCredito,
        observaciones,
        handleChangeObservaciones,
        almacenExhibidorCompra,
        handleChangeAlmacenExhibidorCompra,
        openModalReporte,
        pdfUrl,
        setOpenModalReporte,
        totalConLetra,
        medidasPartida,
        partidasLoadVersion,
        handleCargarMedidasPartida,
        handleActualizarCantidadMedida,
    };

    return (
        <ComprasContext.Provider value={value}>
            {props.children}
        </ComprasContext.Provider>
    );
};

export const useCompras = () => {
    const context = useContext(ComprasContext);
    if (!context) {
        throw new Error("useCompras debe ser usado dentro de un ComprasProvider");
    }
    return context;
};
